<template>
  <div class="search-result-container" ref="resultContainer">
    <!-- 关闭按钮 -->
    <div style="padding: 10px 20px 0; text-align: right">
      <i class="el-icon-close" style="cursor: pointer" @click="clossWorkBench"></i>
    </div>

    <div style="padding: 0 14px 14px">
      <div class="result-toolbar" v-if="articleList.length">
        <div class="result-summary">找到 {{ articleList.length }} 篇资料</div>
        <div class="sort-controls">
          <button
            v-for="option in sortOptions"
            :key="option.key"
            type="button"
            :class="['sort-btn', { active: sortKey === option.key }]"
            @click="changeSort(option.key)"
          >
            <span>{{ option.label }}</span>
            <!-- 相关度固定降序、不可切换方向，不显示箭头；其余排序项显示可切换的升/降箭头 -->
            <i v-if="option.key !== 'relevance'" :class="sortKey === option.key && sortOrder === 'asc' ? 'el-icon-top' : 'el-icon-bottom'"></i>
          </button>
        </div>
      </div>
      <!-- 结果列表 -->
      <div class="result-list">
        <!-- 1. v-for 中保留 index，用于生成序号 -->
        <article class="result-item" v-for="(article, index) in paginatedArticleList" :key="article.sid || article.doi || index">
          <!-- 新增：序号展示区域 -->
          <div class="serial-number">{{ (currentPage - 1) * pageSize + index + 1 }}</div>
          <div class="result-header">
            <div class="title-container">
              <h3 class="result-title">
                <a href="javascript:void(0);" @click="openArticleInRightPanel(article)" rel="noopener noreferrer">
                  {{ article.title || "未知题名" }}
                </a>
              </h3>
              <div class="title-zh" v-if="article.title_zh && article.title_zh.trim()">
                {{ article.title_zh }}
              </div>
            </div>

            <div class="basic-info">
              <span v-if="article.source && article.source.trim()">
                <i class="el-icon-document"></i>
                {{ article.source }}
              </span>
              <el-tag size="mini" style="color: var(--tag-color-blue); background-color: var(--tag-bg-blue)" v-if="article.year || article.pubdate">
                {{ article.year || article.pubdate }}
              </el-tag>
              <span v-if="article.doi && article.doi.trim()" class="doi-inline" :title="cleanDoi(article.doi)">DOI {{ cleanDoi(article.doi) }}</span>
              <el-button v-if="article.doi && article.doi.trim()" size="mini" type="text" @click.stop="copyDoi(article.doi)" icon="el-icon-document-copy">复制DOI</el-button>
            </div>
          </div>

          <div class="metric-tags">
            <span class="metric-tag score-tag" v-if="getScore(article) !== null">评分 {{ formatScore(getScore(article)) }}</span>
            <span class="metric-tag" v-if="getCitationCount(article) !== null">引用次数 {{ formatInteger(getCitationCount(article)) }}</span>
            <span class="metric-tag" v-if="getPublishTimeLabel(article)">发布时间 {{ getPublishTimeLabel(article) }}</span>
          </div>

          <div class="author-info" v-if="article.authors && article.authors.length">
            <span class="info-label">作者：</span>
            <span class="author-names" :title="formatAuthors(article.authors)">{{ formatAuthors(article.authors) }}</span>
          </div>

          <!-- 摘要区域 -->
          <div class="card-abstract" v-if="article.abstract && article.abstract.trim()">
            <p class="abstract-content" :class="{ collapsed: isAbstractCollapsed(getArticleStateIndex(article), 'abstract') }" :title="article.abstract">
              {{ article.abstract }}
            </p>
            <button class="toggle-btn el-button el-button--text el-button--mini" @click="toggleAbstractCollapse(getArticleStateIndex(article), 'abstract')" v-if="article.abstract.length > 160">
              {{ isAbstractCollapsed(getArticleStateIndex(article), "abstract") ? "展开更多" : "收起" }}
            </button>
          </div>

          <button class="toggle-btn zh-toggle-btn el-button el-button--text el-button--mini" @click="toggleAbstractCollapse(getArticleStateIndex(article), 'abstract_zh')" v-if="article.abstract_zh && article.abstract_zh.trim()">
            {{ isAbstractCollapsed(getArticleStateIndex(article), "abstract_zh") ? "查看中文摘要" : "收起中文摘要" }}
          </button>
          <div class="card-abstract abstract-zh" v-if="article.abstract_zh && article.abstract_zh.trim() && !isAbstractCollapsed(getArticleStateIndex(article), 'abstract_zh')">
            <p class="abstract-content" :class="{ collapsed: isAbstractCollapsed(getArticleStateIndex(article), 'abstract_zh') }" :title="article.abstract_zh">
              {{ article.abstract_zh }}
            </p>
          </div>

          <div class="action-bar">
            <el-button size="mini" round class="detail-btn" @click="openArticleInRightPanel(article)">阅读详情</el-button>
          </div>
        </article>
      </div>

      <div class="result-pagination" v-if="sortedArticleList.length > pageSize">
        <span>每页 10 条</span>
        <el-pagination
          background
          small
          layout="prev, pager, next"
          :current-page.sync="currentPage"
          :page-size="pageSize"
          :total="sortedArticleList.length"
        />
      </div>

      <!-- 空状态提示：无结果时显示 -->
      <el-empty description="暂无检索结果，请调整检索条件后重试" v-if="articleList.length === 0"></el-empty>
    </div>
  </div>
</template>

<script>
import { openArticleDetail } from "../utils/openArticleDetail";
/* eslint-disable */
import { cleanDoi } from "@/utils/doiUtils";

export default {
  name: "WorkBench",
  props: {
    content: {
      type: [String, Object],
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      abstractCollapseStates: [],
      sortKey: "relevance",
      sortOrder: "desc",
      currentPage: 1,
      pageSize: 10,
      sortOptions: [
        { key: "relevance", label: "相关度" },
        { key: "citations", label: "引用次数" },
        { key: "published", label: "发布时间" },
      ],
    };
  },
  computed: {
    articleList() {
      try {
        const content = typeof this.content === "string" ? JSON.parse(this.content) : this.content;
        return Array.isArray(content.data_list) ? content.data_list : [];
      } catch (error) {
        console.error("解析文献数据失败:", error);
        return [];
      }
    },
    sortedArticleList() {
      const sortKey = this.sortKey;
      const sortOrder = this.sortOrder;
      const direction = sortOrder === "asc" ? 1 : -1;
      return this.articleList
        .map((article, index) => ({ article, index }))
        .sort((left, right) => {
          const leftValue = this.getSortValue(left.article, sortKey);
          const rightValue = this.getSortValue(right.article, sortKey);
          const leftMissing = leftValue === null || leftValue === undefined || Number.isNaN(leftValue);
          const rightMissing = rightValue === null || rightValue === undefined || Number.isNaN(rightValue);
          if (leftMissing && rightMissing) return left.index - right.index;
          if (leftMissing) return 1;
          if (rightMissing) return -1;
          if (leftValue === rightValue) return left.index - right.index;
          return leftValue > rightValue ? direction : -direction;
        })
        .map(item => item.article);
    },
    paginatedArticleList() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedArticleList.slice(start, start + this.pageSize);
    },
  },
  watch: {
    // 监听文章列表变化，重新初始化折叠状态
    articleList: {
      handler() {
        this.currentPage = 1;
        this.initCollapseStates();
        this.applyQuerySort();
      },
      immediate: true,
    },
  },
  methods: {
    // 后端 query_plan.sort_by 的字段 → 本列表排序选项 key
    mapQuerySortField(field) {
      if (field === "times_cited") return "citations";
      if (field === "year" || field === "pubdate") return "published";
      if (field === "relevance") return "relevance";
      return null;
    },
    // 结果列表默认排序跟随本次查询指定的排序（用户在检索里指定"按引用量"等时右侧同步）
    applyQuerySort() {
      let sortBy = [];
      try {
        const c = typeof this.content === "string" ? JSON.parse(this.content) : this.content;
        if (c && Array.isArray(c.sort_by)) sortBy = c.sort_by;
      } catch (e) {
        /* ignore */
      }
      const first = sortBy[0];
      const key = first && first.field ? this.mapQuerySortField(first.field) : null;
      if (key) {
        this.sortKey = key;
        this.sortOrder = first.order === "asc" ? "asc" : "desc";
      } else {
        this.sortKey = "relevance";
        this.sortOrder = "desc";
      }
    },
    changeSort(key) {
      if (key === "relevance") {
        this.sortKey = key;
        this.sortOrder = "desc";
        this.currentPage = 1;
        return;
      }
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
        this.currentPage = 1;
        return;
      }
      this.sortKey = key;
      this.sortOrder = "desc";
      this.currentPage = 1;
    },

    getSortValue(article, key) {
      if (key === "relevance") return this.getScore(article);
      if (key === "citations") return this.getCitationCount(article);
      if (key === "published") return this.getPublishTimestamp(article);
      return null;
    },

    getScore(article) {
      // 后端规范字段：ResearchSearchResult.score（相关度评分）。严格单字段，不做多名兜底。
      const value = article.score;
      if (value === undefined || value === null || value === "") return null;
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    },

    getCitationCount(article) {
      // 后端规范字段：ResearchSearchResult.times_cited（引用次数）。严格单字段。
      const value = article.times_cited;
      if (value === undefined || value === null || value === "") return null;
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    },

    getPublishTimestamp(article) {
      // 后端规范字段：ResearchSearchResult.pubdate（发布时间字符串，如 "2021" 或完整日期）。
      const raw = article.pubdate;
      if (!raw) return null;
      if (/^\d{4}$/.test(String(raw))) return Number(raw);
      const timestamp = Date.parse(raw);
      if (!Number.isNaN(timestamp)) return timestamp;
      const yearMatch = String(raw).match(/\d{4}/);
      return yearMatch ? Number(yearMatch[0]) : null;
    },

    getPublishTimeLabel(article) {
      // 后端规范字段：ResearchSearchResult.pubdate。严格单字段。
      return article.pubdate || "";
    },

    formatInteger(value) {
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return value;
      return Math.round(numberValue).toLocaleString();
    },

    getArticleStateIndex(article) {
      const index = this.articleList.indexOf(article);
      return index >= 0 ? index : 0;
    },

    // 初始化折叠状态：中英文摘要分别维护，避免按钮互相影响。
    initCollapseStates() {
      this.abstractCollapseStates = this.articleList.map(article => ({
        abstract: !!(article.abstract && article.abstract.trim() && article.abstract.length > 160),
        abstract_zh: true,
      }));
    },

    formatAuthors(authors) {
      // 后端已在 ES 边界将作者归一化为字符串数组，前端直接消费字符串，无需再按对象字段猜取。
      if (!Array.isArray(authors)) return "";
      return authors.filter(author => typeof author === "string" && author).join(", ");
    },

    formatScore(score) {
      const numberScore = Number(score);
      if (Number.isNaN(numberScore)) return score;
      return numberScore.toFixed(2);
    },

    isAbstractCollapsed(index, field) {
      const state = this.abstractCollapseStates[index] || {};
      return !!state[field];
    },

    toggleAbstractCollapse(index, field) {
      const state = this.abstractCollapseStates[index] || {};
      this.$set(this.abstractCollapseStates, index, {
        ...state,
        [field]: !state[field],
      });
    },

    clossWorkBench() {
      this.$emit("clossWorkBench");
    },

    cleanDoi(doi) {
      return cleanDoi(doi);
    },
    copyDoi(doi) {
      const pureDoi = cleanDoi(doi);
      try {
        navigator.clipboard
          .writeText(pureDoi)
          .then(() => {
            this.$message.success("DOI复制成功");
          })
          .catch(() => {
            this.fallbackCopyDoi(pureDoi);
          });
      } catch (e) {
        this.fallbackCopyDoi(pureDoi);
      }
    },
    // 在右侧面板打开文章
    openArticleInRightPanel(article) {
      // 发送兴趣保存请求
      this.$http
        .post(this.globalUrl.interest_save, { interest: article.abstract || article.abstract_zh || article.title })
        .then(response => {
          console.log("兴趣保存成功", response);
        })
        .catch(error => {
          console.error("兴趣保存失败", error);
        });

      openArticleDetail(article);
    },
    fallbackCopyDoi(doi) {
      const input = document.createElement("input");
      input.value = doi;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      this.$message.success("DOI复制成功");
    },
  },
};
</script>

<style scoped>
/* 样式保持不变，新增序号相关样式 */
.search-result-container {
  background-color: var(--bg-front);
  min-height: 0;
  overflow-y: auto;
  height: 100%;
  box-sizing: border-box;
  padding-bottom: 24px;
}
.result-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}
.result-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 0 4px;
  color: var(--label-color);
  font-size: 12px;
}
.result-pagination ::v-deep .el-pagination {
  padding: 0;
}
.result-toolbar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0 10px;
  margin-bottom: 8px;
  background: var(--bg-front);
}
.result-summary {
  color: var(--label-color);
  font-size: 13px;
  white-space: nowrap;
}
.sort-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #dbe7f7;
  border-radius: 14px;
  color: #4b5f7a;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}
.sort-btn:hover,
.sort-btn.active {
  color: var(--a-color);
  border-color: #a9cbff;
  background: #f3f8ff;
}
.sort-btn i {
  font-size: 12px;
}

.result-item {
  margin-bottom: 0;
  min-height: 0;
  transition: all 0.3s ease;
  border-radius: 8px;
  position: relative;
  padding: 14px 112px 12px 58px;
  border: 1px solid #dbeafe;
  background: #fff;
}

/* 新增：序号样式 */
.serial-number {
  position: absolute;
  left: 16px;
  top: 16px;
  width: 28px;
  height: 24px;
  border-radius: 50%;
  background-color: #eef5ff;
  color: var(--a-color);
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}
.result-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.08);
}

.result-header {
  margin-bottom: 5px;
}

.title-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.result-title {
  font-size: 15px;
  color: var(--a-color);
  margin: 0;
  cursor: pointer;
  transition: color 0.2s;
  word-break: break-word;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.result-title a {
  text-decoration: none;
  color: inherit;
}
.result-title a:hover {
  text-decoration: underline;
}

.result-link {
  text-decoration: none;
}
.result-link:hover {
  text-decoration: underline;
}

.result-title:hover {
  color: var(--a-color-hover);
}

.title-zh {
  color: var(--txt-color);
  font-size: 13px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.doi-link {
  font-size: 12px;
  color: var(--label-color);
  text-decoration: underline;
  transition: color 0.2s;
  word-break: break-all;
}

.doi-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.doi-row .doi-link {
  flex: 1;
}

.doi-row ::v-deep .el-button {
  padding: 0;
  line-height: 1;
}

.basic-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  align-items: center;
  color: #8a94a6;
  font-size: 12px;
}
.basic-info span {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.basic-info ::v-deep .el-button {
  padding: 0;
  line-height: 1;
}
.doi-inline {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0 6px;
}
.metric-tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 7px;
  border-radius: 6px;
  color: #4b5f7a;
  background: #f4f7fb;
  font-size: 11px;
  line-height: 20px;
}
.metric-tag.score-tag {
  color: #0f63d8;
  background: #eaf3ff;
}

.author-info {
  display: flex;
  align-items: flex-start;
  margin-bottom: 5px;
  color: var(--label-color);
  flex-wrap: wrap;
  line-height: 1.35;
  font-size: 12px;
}

.info-label {
  font-weight: 500;
  color: var(--label-color);
  margin-right: 8px;
}

.author-names {
  word-break: break-all;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-abstract {
  margin-bottom: 5px;
  font-size: 13px;
  color: var(--label-color);
  line-height: 1.45;
}

.abstract-content {
  margin: 0;
  transition: all 0.3s ease;
}

.collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.toggle-btn {
  color: var(--a-color);
  padding: 1px 0;
  margin-top: 2px;
  cursor: pointer;
  line-height: 1.2;
}
.zh-toggle-btn {
  margin-right: 12px;
}

.toggle-btn:hover {
  color: var(--a-color-hover);
}

.action-bar {
  position: absolute;
  right: 18px;
  top: 56px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 0;
}
.detail-btn {
  color: var(--a-color);
  border-color: #bcd7ff;
  background: #f4f9ff;
}

@media (max-width: 768px) {
  .search-result-container {
    padding: 10px;
  }

  .result-title {
    font-size: 14px;
  }

  .basic-info {
    gap: 4px;
  }

  .el-tag {
    font-size: 11px;
    padding: 2px 6px;
  }

  /* 适配移动端序号样式 */
  .serial-number {
    width: 20px;
    height: 20px;
    font-size: 11px;
    left: 12px;
    top: 12px;
  }

  .result-item {
    padding-left: 32px;
  }
}

.toggle-btn {
  border: none;
}
.toggle-btn:hover {
  border: none;
}
</style>
